import { z } from 'zod';

import { PaymentMethod as GqlPaymentMethod } from '@willow/graphql-iso/src/app';
import { AchPaymentMethods, assertNever, NonAchPaymentMethods, PaymentMethod } from '@willow/types-iso';

export function gqlToIsoPaymentMethod(method: GqlPaymentMethod): PaymentMethod {
  switch (method) {
    case GqlPaymentMethod.Check:
      return 'check';
    case GqlPaymentMethod.Wire:
      return 'wire';
    case GqlPaymentMethod.Phone:
      return 'phone';
    case GqlPaymentMethod.Dwolla:
      return 'dwolla';
    case GqlPaymentMethod.Reversal:
      return 'reversal';
    case GqlPaymentMethod.Reserve:
      return 'reserve';
    case GqlPaymentMethod.ReserveReversal:
      return 'reserveReversal';
    case GqlPaymentMethod.Subsidy:
      return 'subsidy';
    case GqlPaymentMethod.SubsidyReversal:
      return 'subsidyReversal';
    case GqlPaymentMethod.BalanceForward:
      return 'balanceForward';
    case GqlPaymentMethod.PrincipalPrepayment:
      return 'principalPrepayment';
    case GqlPaymentMethod.AuthorizedAch:
      return 'authorizedACH';
    case GqlPaymentMethod.BalanceReallocation:
      return 'balanceReallocation';
    case GqlPaymentMethod.CreditCard:
      return 'creditCard';
    case GqlPaymentMethod.DebitCard:
      return 'debitCard';
    case GqlPaymentMethod.Lockbox:
      return 'lockbox';
    default:
      assertNever(method);
  }
}

// convertEnumToGqlEnum isnt working with "Ach"
export const isoToGqlPaymentMethod = (method: PaymentMethod): GqlPaymentMethod => {
  switch (method) {
    case 'check':
      return GqlPaymentMethod.Check;
    case 'wire':
      return GqlPaymentMethod.Wire;
    case 'phone':
      return GqlPaymentMethod.Phone;
    case 'creditCard':
      return GqlPaymentMethod.CreditCard;
    case 'debitCard':
      return GqlPaymentMethod.DebitCard;
    case 'dwolla':
      return GqlPaymentMethod.Dwolla;
    case 'reversal':
      return GqlPaymentMethod.Reversal;
    case 'reserve':
      return GqlPaymentMethod.Reserve;
    case 'reserveReversal':
      return GqlPaymentMethod.ReserveReversal;
    case 'subsidy':
      return GqlPaymentMethod.Subsidy;
    case 'subsidyReversal':
      return GqlPaymentMethod.SubsidyReversal;
    case 'balanceForward':
      return GqlPaymentMethod.BalanceForward;
    case 'principalPrepayment':
      return GqlPaymentMethod.PrincipalPrepayment;
    case 'authorizedACH':
    case 'repayACH':
    case 'nachaACH':
      return GqlPaymentMethod.AuthorizedAch;
    case 'balanceReallocation':
      return GqlPaymentMethod.BalanceReallocation;
    case 'lockbox':
      return GqlPaymentMethod.Lockbox;
    default:
      assertNever(method);
  }
};

export const isNonAchPaymentMethod = (paymentMethod: PaymentMethod): boolean =>
  z.validate(NonAchPaymentMethods, paymentMethod);

export const isAchPaymentMethod = (paymentMethod: PaymentMethod): boolean =>
  z.validate(AchPaymentMethods, paymentMethod);

/**
 * Payment methods that may be selected for a user-initiated principal
 * curtailment. Excludes:
 *   - allocation-style methods (`reversal`, `reserveReversal`, `balanceForward`)
 *   - the deprecated synthetic `principalPrepayment` method
 *   - the system-internal `reserve` method — system events that need it call
 *     into the snapshot handlers directly.
 *
 * `balanceReallocation` is allowed only as a reallocation from unapplied
 * suspense (the resolver requires `debitSource` alongside it).
 *   - all ACH variants (`dwolla`, `authorizedACH`, `nachaACH`, `repayACH`):
 *     ACH curtailments flow through the regular ACH/Dwolla payment path, not
 *     this manually-entered form.
 *
 * Single source of truth for both the FE dropdown options and the resolver's
 * validation in `initiatePrincipalCurtailmentPayment`.
 */
export const VALID_CURTAILMENT_PAYMENT_METHODS: readonly PaymentMethod[] = [
  'check',
  'wire',
  'phone',
  'creditCard',
  'debitCard',
  'lockbox',
  'balanceReallocation',
];

export const isValidCurtailmentPaymentMethod = (method: PaymentMethod): boolean =>
  VALID_CURTAILMENT_PAYMENT_METHODS.includes(method);
